import { useEffect, useState } from 'react'

import { Button, Field, Icon, Layout, Text, TextField } from '@loadsmart/miranda-react'

import { formatProNumber } from '../presenters/shipmentPresenter'
import { useUpdateProNumber } from './hooks/useUpdateProNumber'

type ProNumberProps = {
  currentProNumber?: string
  shipmentUUID: string
  onUpdate: () => void
}

export function ProNumber({ currentProNumber, onUpdate, shipmentUUID }: ProNumberProps) {
  const [isEditing, setIsEditing] = useState(false)
  const { updateProNumber, updateProNumberStatus } = useUpdateProNumber({
    onUpdate,
  })
  const [draftProNumber, setDraftProNumber] = useState<string | undefined>(currentProNumber)

  useEffect(() => {
    setDraftProNumber(currentProNumber)
    setIsEditing(false)
  }, [currentProNumber])

  const isUpdating = updateProNumberStatus === 'pending'

  if (isEditing) {
    return (
      <Layout.Stack gap="spacing-2">
        <Field style={{ width: 'calc(50% - 8px)' }}>
          <Field.Label id="pro-number-label" size="small">
            Pro number
          </Field.Label>
          <TextField
            value={draftProNumber}
            onChange={event => {
              const { value } = event.target
              setDraftProNumber(value)
            }}
            type="number"
            min="0"
            aria-labelledby="pro-number-label"
            disabled={isUpdating}
          />
        </Field>
        <Layout.Group gap="spacing-2">
          <Button
            variant="secondary"
            size="small"
            onClick={() => {
              setIsEditing(false)
            }}
            disabled={isUpdating}
            type="reset"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={() => {
              updateProNumber(shipmentUUID, draftProNumber)
            }}
            loading={isUpdating}
          >
            Save
          </Button>
        </Layout.Group>
      </Layout.Stack>
    )
  }

  return (
    <Layout.Stack gap="spacing-2" style={{ marginTop: '4px' }}>
      <Text variant="body-sm" color="color-text-tertiary">
        Pro number
      </Text>
      <Layout.Group gap="spacing-1" align="center">
        <Text
          variant="body-md"
          color={currentProNumber ? 'color-text-primary' : 'color-text-placeholder'}
        >
          {formatProNumber(currentProNumber)}
        </Text>
        <Button
          size="small"
          variant="icon-secondary"
          aria-label="Edit pro number"
          onClick={() => setIsEditing(true)}
        >
          <Icon name="pencil" size="small" />
        </Button>
      </Layout.Group>
    </Layout.Stack>
  )
}
